import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { FeedsFrame, Pill, num } from '../components/FeedsNav'
import { useDecideProposals, useProposals } from '../hooks/useFeeds'
import { PROPOSAL_STATUSES, type Proposal, type ProposalStatus, type ProposalType } from '../api/feedsApi'

const ALL = '__all__'

const TYPE_LABEL: Record<ProposalType, string> = { sets: 'Sets', queries: 'Search queries' }

/** What approving does, per kind — shown under the queue so the button is never a surprise. */
const APPROVE_EFFECT: Record<string, string> = {
  retitle: 'Renames the set (name, aesthetic, subject, narrative) and updates its Shopify product.',
  repair: 'Swaps the pieces, re-renders the wall image and re-mints the Shopify product.',
  refresh_image: 'Re-renders the wall image and replaces it on the Shopify product.',
  retire: 'Retires the set and unpublishes its Shopify product.',
  new: 'Shoots the wall and lifestyle photographs (vision-checked), creates the Shopify set product and puts it on its room page. The sets agent applies these itself each night when they pass the gates.',
  negative: 'Adds the keyword to that account\'s shared negative list. The nightly job also applies up to 25 a night on its own; reject one here to keep it out.',
  title_term: 'Queues the phrase for the feed title optimizer for that room. Nothing changes in Ads yet.',
  exact_keyword: 'Queues the query as an exact keyword for the Search-B campaign once it exists.',
  brand_exclusion: 'Queues the name for PMax brand exclusions once PMax exists.',
}

const statusTone = (s: ProposalStatus) =>
  s === 'applied' ? 'green' : s === 'approved' ? 'blue' : s === 'failed' ? 'red' : s === 'proposed' ? 'amber' : 'slate'

const money = (v: unknown, currency?: string | null) => {
  if (typeof v !== 'number') return '—'
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(v)
  } catch {
    return v.toFixed(2)
  }
}

function SetProposal({ p }: { p: Proposal }) {
  const pl = p.payload || {}
  const ev = p.evidence || {}
  return (
    <div className="flex gap-3">
      {p.imageUrl ? <img src={p.imageUrl} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded object-cover" /> : <div className="h-14 w-14 shrink-0 rounded bg-slate-100" />}
      <div className="min-w-0 space-y-1">
        <div className="text-xs text-slate-500"><span className="font-mono">{p.subject}</span>{p.themeSlug && <> · {p.themeSlug}</>}</div>
        {p.kind === 'retitle' && (
          <>
            <div className="text-xs text-slate-500 line-through">{ev.before?.name}</div>
            <div className="font-medium">{ev.title_preview || pl.name}</div>
            <div className="max-w-2xl text-sm text-slate-600">{pl.narrative}</div>
            {ev.persona && <div className="text-xs text-slate-500">for {ev.persona}</div>}
          </>
        )}
        {(p.kind === 'repair' || p.kind === 'refresh_image') && (
          <div className="text-sm">
            {p.setName}
            {Array.isArray(pl.replaced) && pl.replaced.map((r: any) => (
              <div key={`${r.out}-${r.in}`} className="text-xs text-slate-600">piece {r.out} → {r.in}{r.score != null && ` (score ${Number(r.score).toFixed(2)})`}</div>
            ))}
          </div>
        )}
        {p.kind === 'retire' && <div className="text-sm">{p.setName}{ev.reason && <div className="text-xs text-slate-500">{ev.reason}</div>}</div>}
        {p.kind === 'new' && (
          <>
            <div className="font-medium">{ev.title_preview || p.setName}</div>
            {ev.copy?.narrative && <div className="max-w-2xl text-sm text-slate-600">{ev.copy.narrative}</div>}
            <div className="text-xs text-slate-500">
              {ev.kind} set · pieces {(pl.piece_artwork_ids || []).join(', ')}
              {ev.cohesion?.score != null && ` · cohesion ${Number(ev.cohesion.score).toFixed(2)}`}
              {ev.fresh_seed && ' · seeded with new work'}
              {ev.replaces && ` · replaces ${ev.replaces}`}
            </div>
          </>
        )}
        {ev.rejected_by_gate && <div className="text-xs text-amber-700">Gate: {ev.rejected_by_gate}</div>}
        {ev.image_check && (
          <div className="text-xs text-slate-500">
            Image check: {Object.entries(ev.image_check).filter(([k]) => k !== 'checked_at').map(([k, v]: [string, any]) => `${k} ${v?.pass ? 'pass' : `fail (${(v?.reasons || []).join('; ')})`}`).join(' · ')}
          </div>
        )}
        {p.error && <div className="text-xs text-red-700">{p.error}</div>}
      </div>
    </div>
  )
}

function QueryProposal({ p }: { p: Proposal }) {
  const t = p.target || {}
  const ev = p.evidence || {}
  return (
    <div className="space-y-1">
      <div className="font-medium">
        {p.subject}
        {t.match_type && <span className="ml-2 text-xs font-normal text-slate-500">{String(t.match_type).toLowerCase()}</span>}
      </div>
      <div className="text-xs text-slate-500">
        {p.account && <>{p.account.name || p.account.customerId} · </>}{ev.class}{t.reason && t.reason !== ev.class && <> · {t.reason}</>}{t.room_slug && <> · room {t.room_slug}</>}{t.list && <> · list {t.list}</>}{t.artist && <> · {t.artist}</>}
      </div>
      {Array.isArray(ev.queries) && ev.queries.length > 0 && (
        <div className="max-w-2xl text-xs text-slate-600">blocks e.g. {ev.queries.slice(0, 5).join(' · ')}</div>
      )}
      {p.error && <div className="text-xs text-red-700">{p.error}</div>}
    </div>
  )
}

export function ProposalsPage() {
  const [params, setParams] = useSearchParams()
  const type = (params.get('type') as ProposalType) || 'sets'
  const status = (params.get('status') as ProposalStatus) || 'proposed'
  const kind = params.get('kind') || undefined
  const { data, isLoading } = useProposals({ type, status, kind })
  const decide = useDecideProposals()
  const [selected, setSelected] = useState<number[]>([])

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key === 'type') next.delete('kind')
    setParams(next, { replace: true })
    setSelected([])
  }

  const counts = data?.counts ?? []
  const openCount = (t: ProposalType) => counts.filter((c) => c.type === t && c.status === 'proposed').reduce((n, c) => n + c.n, 0)
  const kinds = useMemo(() => Array.from(new Set(counts.filter((c) => c.type === type).map((c) => c.kind))).sort(), [counts, type])
  const rows = data?.data ?? []
  const actionable = status === 'proposed'
  const allSelected = rows.length > 0 && selected.length === rows.length
  const run = (ids: number[], decision: 'approve' | 'reject') => {
    decide.mutate({ type, ids, decision })
    setSelected([])
  }

  return (
    <FeedsFrame
      title="Proposals"
      description="Everything the agents suggest, waiting for a decision: set changes from the groomer and retitler, and paid-search actions mined from search terms. Nothing changes until it is approved."
    >
      <div className="flex flex-wrap items-center gap-2">
        {(['sets', 'queries'] as ProposalType[]).map((t) => (
          <Button key={t} size="sm" variant={t === type ? 'default' : 'outline'} onClick={() => set('type', t)}>
            {TYPE_LABEL[t]}
            <span className="ml-2 rounded-full bg-white/20 px-1.5 text-xs tabular-nums">{num(openCount(t))}</span>
          </Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Select value={status} onValueChange={(v) => set('status', v)}>
            <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{PROPOSAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={kind ?? ALL} onValueChange={(v) => set('kind', v === ALL ? null : v)}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>all kinds</SelectItem>
              {kinds.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-base">{TYPE_LABEL[type]} · {status}</CardTitle>
            <CardDescription className="max-w-3xl">
              {kind ? APPROVE_EFFECT[kind] : type === 'sets'
                ? 'Approving applies the change to the set and its Shopify product in the background.'
                : 'Approving a negative pushes it to Google Ads; other kinds are queued for the stage that uses them.'}
            </CardDescription>
          </div>
          {actionable && selected.length > 0 && (
            <div className="flex shrink-0 gap-2">
              <Button size="sm" disabled={decide.isPending} onClick={() => run(selected, 'approve')}>Approve {selected.length}</Button>
              <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => run(selected, 'reject')}>Reject {selected.length}</Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {actionable && (
                  <TableHead className="w-10">
                    <Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? rows.map((r) => r.id) : [])} aria-label="Select all" />
                  </TableHead>
                )}
                <TableHead className="w-28">Kind</TableHead>
                <TableHead>Proposal</TableHead>
                {type === 'queries' && <TableHead className="text-right">Evidence</TableHead>}
                <TableHead>Status</TableHead>
                {actionable && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={6}><Skeleton className="h-10 w-full" /></TableCell></TableRow>}
              {!isLoading && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Nothing {status}.</TableCell></TableRow>
              )}
              {rows.map((p) => (
                <TableRow key={p.id}>
                  {actionable && (
                    <TableCell className="align-top">
                      <Checkbox
                        checked={selected.includes(p.id)}
                        onCheckedChange={(v) => setSelected((s) => (v ? [...s, p.id] : s.filter((id) => id !== p.id)))}
                        aria-label={`Select ${p.subject}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="align-top"><Pill tone="slate">{p.kind}</Pill><div className="mt-1 text-[11px] text-slate-400">#{p.id}</div></TableCell>
                  <TableCell className="align-top">{p.type === 'sets' ? <SetProposal p={p} /> : <QueryProposal p={p} />}</TableCell>
                  {type === 'queries' && (
                    <TableCell className="whitespace-nowrap text-right align-top text-xs tabular-nums">
                      <div>{num(p.evidence.impressions)} impr · {num(p.evidence.clicks)} clicks</div>
                      <div>{money(p.evidence.cost, p.evidence.currency || p.account?.currency)} · {num(p.evidence.conversions)} conv</div>
                      {p.evidence.window && <div className="text-slate-400">{p.evidence.source === 'history' && 'history '}{p.evidence.window.from} → {p.evidence.window.to}</div>}
                      {!p.evidence.window && p.evidence.source === 'history' && <div className="text-slate-400">all-time history</div>}
                    </TableCell>
                  )}
                  <TableCell className="align-top"><Pill tone={statusTone(p.status)}>{p.status}</Pill></TableCell>
                  {actionable && (
                    <TableCell className="whitespace-nowrap text-right align-top">
                      <Button size="sm" variant="ghost" className="h-8" disabled={decide.isPending} onClick={() => run([p.id], 'approve')}>Approve</Button>
                      <Button size="sm" variant="ghost" className="h-8 text-red-700" disabled={decide.isPending} onClick={() => run([p.id], 'reject')}>Reject</Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </FeedsFrame>
  )
}
